import { EXPENSE_VOID_MESSAGES } from "./expenses";

/**
 * French text for the translation keys scanifood_api sends back in error
 * responses (SFRESTO-163). The API has no translation catalogue: violation
 * `message`s, `detail` and `hydra:description` carry the raw key (validation
 * errors prefix it with the property path, `title: validation.…`).
 *
 * Keys come from scanifood_api `src/` (constraints, validators, processors,
 * `cash_register.*` violations).
 * Add a line here when the API gains a key the dashboard can hit; an unknown
 * key falls back to the caller's French message, never to the raw key.
 */
export const API_ERROR_MESSAGES: Record<string, string> = {
  // --- Security / session ---------------------------------------------------
  "security.access_denied":
    "Vous n'avez pas l'autorisation d'effectuer cette action.",
  "security.error.auth_code_expired": "Code expiré. Demandez un nouveau code.",
  "security.error.auth_code_invalid": "Code incorrect.",
  "security.error.auth_code_required": "Saisissez le code reçu.",
  "security.error.email_or_phone_required":
    "Saisissez votre email ou votre numéro de téléphone.",
  "security.error.employee_without_restaurant":
    "Votre compte n'est plus rattaché à aucun restaurant.",
  "security.error.manager_without_restaurant":
    "Votre compte n'est plus rattaché à aucun restaurant.",
  "security.error.owner_without_franchise":
    "Votre compte n'est plus rattaché à aucune franchise.",
  "security.error.invalid_origin":
    "Connexion refusée depuis cette application.",
  "security.error.origin_header_not_found":
    "Connexion refusée depuis cette application.",
  "security.error.unauthorized_origin":
    "Connexion refusée depuis cette application.",
  "security.error.unauthorized_app_access":
    "Votre compte n'a pas accès à cette application.",
  "security.error.login_failed": "Échec de la connexion. Veuillez réessayer.",
  "security.error.restaurant_disabled":
    "Ce restaurant est désactivé. Contactez le support ScaniFood.",
  "security.error.too_many_attempts":
    "Trop de tentatives. Patientez quelques minutes avant de réessayer.",
  "security.error.too_many_login_requests":
    "Trop de demandes de code. Réessayez plus tard.",
  "security.error.user.not_found":
    "Le compte utilisateur associé à l'email ou au numéro de téléphone fourni est introuvable.",
  "security.error.user.identifier_change_forbidden":
    "Vous ne pouvez pas modifier l'identifiant de ce compte.",
  "security.error.user_inactive":
    "Votre compte a été désactivé. Contactez le responsable de votre restaurant.",
  "security.error.identifier_change.too_many_requests":
    "Patientez une minute avant de demander un nouveau code.",
  "validation.login.error.email_or_phone.not_blank":
    "Saisissez votre email ou votre numéro de téléphone.",
  "validation.login.error.country.invalid": "Pays invalide.",

  // --- Expenses ---------------------------------------------------------------
  "expense.drawer.no_open_session":
    "Aucune session de caisse ouverte : ouvrez la caisse avant de payer depuis le tiroir.",
  "expense.drawer.requires_cash_movement_permission":
    "Vous n'avez pas l'autorisation de sortir de l'argent de la caisse.",
  ...EXPENSE_VOID_MESSAGES,
  "validation.expense.error.already_voided": "Cette dépense est déjà annulée.",
  "validation.expense.error.amount.positive":
    "Le montant doit être supérieur à zéro.",
  "validation.expense.error.category.archived":
    "Cette catégorie est archivée. Choisissez-en une autre.",
  "validation.expense.error.category.foreign":
    "Cette catégorie n'appartient pas à ce restaurant.",
  "validation.expense.error.category.required": "Choisissez une catégorie.",
  "validation.expense.error.incurred_at.required":
    "Indiquez la date de la dépense.",
  "validation.expense.error.payment_method.required":
    "Choisissez un moyen de paiement.",
  "validation.expense.error.void_reason_required":
    "Indiquez le motif de l'annulation.",
  "validation.expense.error.void_reason.too_long":
    "Le motif d'annulation est trop long.",
  "validation.expense.error.payee.too_long":
    "Le nom du bénéficiaire est trop long.",
  "validation.expense.error.receipt.foreign":
    "Ce justificatif n'appartient pas à ce restaurant.",
  "validation.expense_category.error.label.duplicate":
    "Une catégorie porte déjà ce nom.",
  "validation.expense_category.error.label.required":
    "Le nom de la catégorie est obligatoire.",
  "validation.expense_category.error.label.too_long":
    "Le nom de la catégorie est trop long.",
  "validation.expense_category.error.label.not_allowed_for_system":
    "Une catégorie système ne peut pas être renommée.",
  "validation.expense_category.error.system.archive_forbidden":
    "Une catégorie système ne peut pas être archivée.",
  "validation.expense_category.error.system.rename_forbidden":
    "Une catégorie système ne peut pas être renommée.",
  "validation.expense_category.error.translation_key.not_allowed_for_custom":
    "Catégorie invalide.",
  "validation.expense_category.error.translation_key.required":
    "Catégorie invalide.",

  // --- Cash register -----------------------------------------------------------
  "cash_register.movement.type_required": "Sélectionnez un type de mouvement.",
  "cash_register.movement.amount_required": "Le montant est requis.",
  "cash_register.movement.amount_positive":
    "Le montant doit être supérieur à zéro.",
  "cash_register.movement.reason_required":
    "La raison est obligatoire pour ce type de mouvement.",
  "cash_register.movement.correction_direction_required":
    "Choisissez le sens de la correction (débit ou crédit).",
  "cash_register.movement.session_closed":
    "La caisse est déjà fermée — le mouvement ne peut pas être enregistré.",
  "cash_register.movement.order_not_in_restaurant":
    "Cette commande n'appartient pas à ce restaurant.",
  "cash_register.session.already_closed": "Cette caisse est déjà fermée.",

  // --- Purchase orders, suppliers, stock --------------------------------------
  "validation.purchase_order.error.already_paid":
    "Ce bon de commande est déjà payé.",
  "validation.purchase_order.error.not_payable":
    "Ce bon de commande ne peut pas encore être payé.",
  "validation.purchase_order.error.payment_method.required":
    "Choisissez un moyen de paiement.",
  "validation.purchase_order.error.expense_already_recorded":
    "Une dépense a déjà été enregistrée pour ce bon de commande.",
  "validation.purchase_order.error.drawer_requires_expense":
    "Un paiement depuis la caisse doit générer une dépense.",
  "validation.purchase_order.error.cogs_category_missing":
    "La catégorie de dépense « Achats de marchandises » est introuvable.",
  "validation.purchase_order.error.cancel.already_cancelled":
    "Ce bon de commande est déjà annulé.",
  "validation.purchase_order.error.delete.requires_draft_or_cancelled":
    "Seul un bon de commande en brouillon ou annulé peut être supprimé.",
  "validation.purchase_order.error.lines.frozen_after_receipt":
    "Les lignes ne peuvent plus être modifiées après une réception.",
  "validation.purchase_order.error.lines.not_empty":
    "Ajoutez au moins une ligne au bon de commande.",
  "validation.purchase_order.error.ingredient.not_in_restaurant":
    "Cet ingrédient n'appartient pas à ce restaurant.",
  "validation.purchase_order.error.supplier.not_in_restaurant":
    "Ce fournisseur n'appartient pas à ce restaurant.",
  "validation.purchase_order.error.supplier.frozen_after_draft":
    "Le fournisseur ne peut plus être modifié une fois le bon envoyé.",
  "validation.purchase_order.error.receive.requires_sent_or_partially_received":
    "Seul un bon de commande envoyé peut être réceptionné.",
  "validation.purchase_order_item.error.package_quantity_ordered.positive":
    "La quantité commandée doit être supérieure à zéro.",
  "validation.purchase_order_item.error.quantity_ordered.positive":
    "La quantité commandée doit être supérieure à zéro.",
  "validation.purchase_order_item.error.package_unit_price.not_negative":
    "Le prix unitaire ne peut pas être négatif.",
  "validation.purchase_order_item.error.unit_price.not_negative":
    "Le prix unitaire ne peut pas être négatif.",
  "validation.purchase_order_item.error.packaging.ingredient_mismatch":
    "Ce conditionnement ne correspond pas à l'ingrédient.",
  "validation.bulk_purchase.error.ingredient.not_in_restaurant":
    "Un ingrédient n'appartient pas à ce restaurant.",
  "validation.bulk_purchase.error.supplier.not_in_restaurant":
    "Un fournisseur n'appartient pas à ce restaurant.",
  "validation.bulk_purchase.error.orders.not_empty":
    "Ajoutez au moins un bon de commande.",
  "validation.bulk_purchase.error.orders.too_many":
    "Trop de bons de commande en une fois.",
  "validation.goods_receipt.error.quantity.invalid": "Quantité reçue invalide.",
  "validation.recipe.error.ingredient.not_in_restaurant":
    "Cet ingrédient n'appartient pas à ce restaurant.",
  "validation.supplier.error.name.exists":
    "Un fournisseur avec ce nom existe déjà",
  "validation.ingredient.error.name.exists":
    "Un ingrédient avec ce nom existe déjà",
  "validation.ingredient_packaging.error.name.exists":
    "Un conditionnement avec ce nom existe déjà",
  "validation.stock_count.error.counted_quantity.invalid":
    "Quantité comptée invalide.",
  "validation.stock_count.error.counted_package_quantity.invalid":
    "Quantité comptée invalide.",
  "validation.menu_item_ingredient.error.ingredient.already_used":
    "Cet ingrédient est déjà dans la recette.",

  // --- Orders -----------------------------------------------------------------
  "validation.order.error.access_code.invalid_length":
    "Le code d'accès est invalide.",
  "validation.order.error.access_code.invalid_or_expired":
    "Le code d'accès est invalide ou expiré.",
  "validation.order.error.access_code.not_blank": "Saisissez le code d'accès.",
  "validation.order.error.customer.required_for_delivery":
    "Un client est obligatoire pour une livraison.",
  "validation.order.error.delivery_address.required_for_delivery":
    "L'adresse de livraison est obligatoire.",
  "validation.order.error.items.not_empty":
    "Ajoutez au moins un article à la commande.",
  "validation.order.error.menu.not_blank":
    "Aucun menu actif pour cette commande.",
  "validation.order.error.placement_not_allowed":
    "Vous n'avez pas l'autorisation de créer ce type de commande.",
  "validation.order.error.restaurant_table.required_for_dine_in":
    "Choisissez une table pour une commande sur place.",
  "validation.order.error.status.invalid": "Statut de commande invalide.",
  "validation.order.error.status.transition_not_allowed":
    "Ce changement de statut n'est pas possible pour cette commande.",
  "validation.order.error.status.collected_payments_not_refunded":
    "Cette commande a des paiements encaissés : remboursez-les avant de l'annuler.",
  "validation.order.error.table_code.not_blank":
    "Saisissez le code de la table.",
  "validation.order.error.table.mismatch":
    "Cette table ne correspond pas à la commande.",
  "validation.order.error.pos_source_requires_staff":
    "Seul le personnel du restaurant peut créer une commande depuis la caisse.",
  "validation.order.error.restaurant_not_available":
    "Ce restaurant ne prend pas de commandes pour le moment.",
  "validation.order.error.menu.wrong_restaurant":
    "Ce menu n'appartient pas à ce restaurant.",
  "validation.order.error.menu_item.wrong_menu":
    "Un des plats ne fait pas partie du menu de cette commande. Videz le panier et réessayez.",
  "validation.order.error.menu_item.wrong_restaurant":
    "Un des plats ne fait pas partie de ce restaurant.",
  "validation.order.error.menu_combo.wrong_menu":
    "Un des menus combo ne fait pas partie du menu de cette commande. Videz le panier et réessayez.",
  "validation.order.error.menu_formula.wrong_restaurant":
    "Une des formules ne fait pas partie de ce restaurant.",
  "validation.order.error.daily_dish.wrong_restaurant":
    "Un des plats du jour ne fait pas partie de ce restaurant.",
  "validation.order.error.restaurant_table.wrong_restaurant":
    "Cette table n'appartient pas à ce restaurant.",
  "validation.order.merge.different_restaurants":
    "Impossible de fusionner des commandes de restaurants différents.",
  "validation.order.merge.different_tables":
    "Impossible de fusionner des commandes de tables différentes.",
  "validation.order.merge.min_orders":
    "Sélectionnez au moins deux commandes à fusionner.",
  "validation.order.merge.no_table":
    "Seules les commandes sur place (avec une table) peuvent être fusionnées.",
  "validation.order.merge.status_not_mergeable":
    "Une des commandes ne peut plus être fusionnée.",
  "validation.order.update.add_only.cannot_remove_item":
    "Cette commande est déjà prête ou servie : vous pouvez seulement ajouter des articles, pas en retirer.",
  "validation.order.update.status_not_editable":
    "Cette commande ne peut plus être modifiée dans son état actuel.",
  "validation.order.update.unknown_item":
    "Un des articles ne fait pas partie de cette commande.",
  "validation.orders.status.not_blank": "Le statut est obligatoire.",
  "validation.order_item.error.daily_dish_with_options":
    "Un plat du jour ne peut pas avoir d'options.",
  "validation.order_item.error.daily_dish_not_available":
    "Ce plat du jour n'est pas disponible aujourd'hui.",
  "validation.order_item.error.missing_item":
    "Un article de la commande est vide.",
  "validation.order_item.error.multiple_items":
    "Un article de la commande est invalide.",
  "validation.order_item.error.quantity.invalid": "Quantité invalide.",
  "validation.order_item.error.quantity.not_blank":
    "La quantité est obligatoire.",
  "validation.order_item.error.selected_menu_item_option_values.duplicate_option_value":
    "Une option a été choisie deux fois.",
  "validation.order_item.error.selected_menu_item_option_values.invalid_option_value":
    "Une option choisie n'est pas valable pour ce plat.",
  "validation.order_item.error.selected_menu_item_option_values.missing_required_option":
    "Une option obligatoire n'a pas été choisie.",
  "validation.order_item.error.selected_menu_item_option_values.too_many_selections":
    "Trop d'options choisies.",
  "validation.order_item.error.status.invalid": "Statut d'article invalide.",
  "validation.order_item.error.status.not_blank": "Le statut est obligatoire.",
  "validation.order_item.error.status.transition_not_allowed":
    "Ce changement de statut n'est pas possible pour cet article.",
  "validation.payment.error.cash_register_session.required":
    "Aucune session de caisse ouverte : ouvrez la caisse avant d'encaisser.",
  "validation.menu_combo_order_item.error.invalid_menu_item_for_component":
    "Un choix du combo n'est pas valable.",
  "validation.menu_combo_order_item.error.menu_combo.not_blank":
    "Le combo est obligatoire.",
  "validation.menu_combo_order_item.error.missing_required_option":
    "Une option obligatoire du combo n'a pas été choisie.",
  "validation.menu_combo_order_item.error.quantity.invalid":
    "Quantité invalide.",
  "validation.menu_combo_order_item.error.quantity.not_blank":
    "La quantité est obligatoire.",
  "validation.menu_combo_order_item.error.required_component_missing":
    "Un élément obligatoire du combo n'a pas été choisi.",
  "validation.menu_combo_order_item.error.selections.not_empty":
    "Faites vos choix pour le combo.",
  "validation.menu_combo_order_item.error.too_many_selections_for_component":
    "Trop de choix pour un élément du combo.",
  "validation.menu_formula_order_item.error.duplicate_option":
    "Une option a été choisie deux fois.",
  "validation.menu_formula_order_item.error.formula_not_active":
    "Cette formule n'est pas disponible aujourd'hui.",
  "validation.menu_formula_order_item.error.invalid_option":
    "Une option choisie n'est pas valable pour cette formule.",
  "validation.menu_formula_order_item.error.invalid_selection_for_component":
    "Un choix de la formule n'est pas valable.",
  "validation.menu_formula_order_item.error.menu_formula.not_blank":
    "La formule est obligatoire.",
  "validation.menu_formula_order_item.error.missing_required_option":
    "Une option obligatoire de la formule n'a pas été choisie.",
  "validation.menu_formula_order_item.error.quantity.invalid":
    "Quantité invalide.",
  "validation.menu_formula_order_item.error.quantity.not_blank":
    "La quantité est obligatoire.",
  "validation.menu_formula_order_item.error.required_component_missing":
    "Un élément obligatoire de la formule n'a pas été choisi.",
  "validation.menu_formula_order_item.error.selections.not_empty":
    "Faites vos choix pour la formule.",
  "validation.menu_formula_order_item.error.too_many_option_selections":
    "Trop d'options choisies pour la formule.",
  "validation.menu_formula_order_item.error.too_many_selections_for_component":
    "Trop de choix pour un élément de la formule.",

  // --- Menus, dishes, options, combos, formulas -------------------------------
  "validation.menu.error.restaurant.not_blank":
    "Le restaurant est obligatoire.",
  "validation.menu.error.status.active_menu_exists":
    "Un autre menu est déjà actif. Désactivez-le d'abord.",
  "validation.menu.error.status.invalid": "Statut de menu invalide.",
  "validation.menu.error.title.exists": "Ce menu existe déjà",
  "validation.menu.error.title.not_blank": "Le nom du menu est obligatoire.",
  "validation.menus.status.not_blank": "Le statut est obligatoire.",
  "validation.menu_section.error.menu.not_blank": "Le menu est obligatoire.",
  "validation.menu_section.error.title.exists":
    "Une section porte déjà ce nom dans ce menu.",
  "validation.menu_section.error.title.not_blank":
    "Le nom de la section est obligatoire.",
  "validation.menu_item.error.base_price.invalid": "Prix invalide.",
  "validation.menu_item.error.base_price.not_blank": "Le prix est obligatoire.",
  "validation.menu_item.error.menu_section.not_blank":
    "Choisissez une section.",
  "validation.menu_item.error.price_override.invalid": "Prix invalide.",
  "validation.menu_item.error.title.exists":
    "Un plat porte déjà ce nom dans ce menu.",
  "validation.menu_item.error.title.not_blank":
    "Le nom du plat est obligatoire.",
  "validation.product.error.base_price.invalid": "Prix invalide.",
  "validation.product.error.base_price.not_blank": "Le prix est obligatoire.",
  "validation.product.error.title.exists": "Un plat porte déjà ce nom.",
  "validation.product.error.title.not_blank": "Le nom du plat est obligatoire.",
  "validation.menu_item_option.error.manager.no_restaurant":
    "Aucun restaurant sélectionné.",
  "validation.menu_item_option.error.title.not_blank":
    "Le nom de l'option est obligatoire.",
  "validation.menu_item_option.error.type.not_blank":
    "Le type d'option est obligatoire.",
  "validation.menu_item_option_value.error.base_price.invalid":
    "Prix invalide.",
  "validation.menu_item_option_value.error.menu_item_option.not_blank":
    "L'option est obligatoire.",
  "validation.menu_item_option_value.error.title.exists":
    "Ce choix existe déjà pour cette option.",
  "validation.menu_item_option_value.error.title.not_blank":
    "Le nom du choix est obligatoire.",
  "validation.item_option.error.already_on_dish":
    "Ce plat a déjà cette option : modifiez l'option existante du plat.",
  "validation.item_option.error.duplicate.bound_to_dish":
    "Ce plat a déjà cette option : elle ne peut pas être dupliquée sur le même plat.",
  "validation.item_option.error.menu_item_option.not_blank":
    "L'option est obligatoire.",
  "validation.item_option_price.error.menu_item_option_value.not_blank":
    "Le choix est obligatoire.",
  "validation.item_option_price.error.price_modifier.invalid": "Prix invalide.",
  "validation.item_option_price.error.price_modifier.not_blank":
    "Le prix est obligatoire.",
  "validation.value_dependent_price.error.depends_on_value.not_blank":
    "Le choix de référence est obligatoire.",
  "validation.value_dependent_price.error.price_modifier.invalid":
    "Prix invalide.",
  "validation.value_dependent_price.error.price_modifier.not_blank":
    "Le prix est obligatoire.",
  "validation.bundle.error.duplicate_pair": "Cette association existe déjà.",
  "validation.daily_dish.error.days_of_week.invalid_day": "Jour invalide.",
  "validation.daily_dish.error.days_of_week.not_blank":
    "Choisissez au moins un jour.",
  "validation.daily_dish.error.days_of_week.required_when_active":
    "Choisissez au moins un jour pour activer ce plat du jour.",
  "validation.daily_dish.error.name.required_when_no_menu_item":
    "Le nom du plat est obligatoire.",
  "validation.daily_dish.error.price.invalid": "Prix invalide.",
  "validation.menu_combo.error.menu.not_blank": "Le menu est obligatoire.",
  "validation.menu_combo.error.name.exists": "Un combo avec ce nom existe déjà",
  "validation.menu_combo.error.name.not_blank":
    "Le nom du combo est obligatoire.",
  "validation.menu_combo.error.price.greater_than_zero":
    "Le prix doit être supérieur à zéro.",
  "validation.menu_combo.error.price.not_blank": "Le prix est obligatoire.",
  "validation.menu_combo_component.error.menu_combo.not_blank":
    "Le combo est obligatoire.",
  "validation.menu_combo_component.error.name.exists":
    "Un composant avec ce nom existe déjà",
  "validation.menu_combo_component.error.name.not_blank":
    "Le nom de l'élément est obligatoire.",
  "validation.menu_combo_component.error.quantity.greater_than_zero":
    "La quantité doit être supérieure à zéro.",
  "validation.menu_formula.error.days_of_week.invalid_day": "Jour invalide.",
  "validation.menu_formula.error.days_of_week.not_blank":
    "Choisissez au moins un jour.",
  "validation.menu_formula.error.days_of_week.required_when_active":
    "Choisissez au moins un jour pour activer cette formule.",
  "validation.menu_formula.error.name.exists":
    "Une formule avec ce nom existe déjà",
  "validation.menu_formula.error.name.not_blank":
    "Le nom de la formule est obligatoire.",
  "validation.menu_formula.error.price.greater_than_zero":
    "Le prix doit être supérieur à zéro.",
  "validation.menu_formula.error.price.not_blank": "Le prix est obligatoire.",
  "validation.menu_formula_component.error.menu_formula.not_blank":
    "La formule est obligatoire.",
  "validation.menu_formula_component.error.name.exists":
    "Un élément porte déjà ce nom dans cette formule.",
  "validation.menu_formula_component.error.name.not_blank":
    "Le nom de l'élément est obligatoire.",
  "validation.menu_formula_component.error.quantity.greater_than_zero":
    "La quantité doit être supérieure à zéro.",
  "validation.menu_formula_component_item.error.component.not_blank":
    "L'élément est obligatoire.",
  "validation.menu_formula_component_item.error.name.required_when_no_menu_item":
    "Le nom du choix est obligatoire.",
  "validation.menu_formula_component_item.error.price.invalid":
    "Prix invalide.",
  "validation.formula_component_item_option.error.menu_formula_component_item.not_blank":
    "Le choix est obligatoire.",
  "validation.formula_component_item_option.error.menu_item_option.not_blank":
    "L'option est obligatoire.",
  "validation.formula_component_item_option_price.error.menu_item_option_value.not_blank":
    "Le choix est obligatoire.",
  "validation.formula_component_item_option_price.error.price_modifier.invalid":
    "Prix invalide.",
  "validation.formula_component_item_option_price.error.price_modifier.not_blank":
    "Le prix est obligatoire.",
  "validation.menu_import.error.file.invalid_type":
    "Format de fichier non pris en charge.",
  "validation.menu_import.error.file.not_null": "Aucun fichier sélectionné.",
  "validation.menu_import.error.restaurant.not_blank":
    "Le restaurant est obligatoire.",
  "validation.media_object.error.file.mime_type":
    "Format d'image non pris en charge. Utilisez une image JPEG, PNG, WebP ou GIF.",
  "validation.media_object.error.file.max_size":
    "Image trop lourde : la taille maximale est de 2 Mo.",
  "validation.media_object.error.file.not_null": "Aucune image sélectionnée.",

  // --- Restaurant, tables, payment methods, customers -------------------------
  "validation.restaurant.error.name.exists": "Un restaurant porte déjà ce nom.",
  "validation.restaurant.error.name.not_blank":
    "Le nom du restaurant est obligatoire.",
  "validation.restaurant.name.not_blank":
    "Le nom du restaurant est obligatoire.",
  "validation.restaurant.error.country.not_blank": "Le pays est obligatoire.",
  "validation.restaurant.country.not_blank": "Le pays est obligatoire.",
  "validation.restaurant.error.currency.not_blank":
    "La devise est obligatoire.",
  "validation.restaurant.currency.not_blank": "La devise est obligatoire.",
  "validation.restaurant.error.timezone.invalid": "Fuseau horaire invalide.",
  "validation.restaurant.error.timezone.not_blank":
    "Le fuseau horaire est obligatoire.",
  "validation.restaurant.error.ownership.invalid":
    "Ce restaurant n'appartient pas à votre franchise.",
  "validation.restaurant.not_own_restaurant":
    "Ce restaurant n'appartient pas à votre franchise.",
  "validation.restaurant.error.status.invalid": "Statut invalide.",
  "validation.restaurant.error.status.not_null": "Le statut est obligatoire.",
  "validation.restaurant.error.status.use_delete_instead":
    "Utilisez la suppression pour retirer ce restaurant.",
  "validation.restaurant.activation.error.not_in_demo_mode":
    "Ce restaurant n'est pas en mode démo.",
  "validation.restaurant.manager.not_blank": "Le manager est obligatoire.",
  "validation.restaurant.manager.already_manager":
    "Cet utilisateur est déjà manager de ce restaurant.",
  "validation.restaurant.manager.not_franchise_member":
    "Cet utilisateur ne fait pas partie de la franchise.",
  "validation.restaurant_manager.error.already_manages_restaurant":
    "Cet utilisateur est déjà manager de ce restaurant.",
  "validation.restaurant_manager.error.cannot_remove_primary":
    "Le manager principal ne peut pas être retiré.",
  "validation.restaurant_manager.error.manager_and_phone_exclusive":
    "Choisissez un utilisateur existant ou saisissez un numéro, pas les deux.",
  "validation.restaurant_manager.error.manager_or_phone_required":
    "Choisissez un utilisateur ou saisissez un numéro de téléphone.",
  "validation.restaurant_manager.error.not_franchise_employee":
    "Cet utilisateur ne fait pas partie de la franchise.",
  "validation.restaurant_manager.error.not_restaurant_employee":
    "Cet utilisateur ne fait pas partie de ce restaurant.",
  "validation.restaurant_franchise.error.name.exists":
    "Une franchise porte déjà ce nom.",
  "validation.restaurant_franchise.error.name.not_blank":
    "Le nom de la franchise est obligatoire.",
  "validation.restaurant_franchise.error.owner.not_blank":
    "Le propriétaire est obligatoire.",
  "validation.franchise.activation.error.not_in_demo_mode":
    "Cette franchise n'est pas en mode démo.",
  "validation.franchise.error.status.use_delete_instead":
    "Utilisez la suppression pour retirer cette franchise.",
  "validation.franchise_owner.error.already_owner":
    "Cet utilisateur est déjà propriétaire de cette franchise.",
  "validation.franchise_owner.error.cannot_remove_primary":
    "Le propriétaire principal ne peut pas être retiré.",
  "validation.franchise_owner.error.owner_and_phone_exclusive":
    "Choisissez un utilisateur existant ou saisissez un numéro, pas les deux.",
  "validation.franchise_owner.error.owner_or_phone_required":
    "Choisissez un utilisateur ou saisissez un numéro de téléphone.",
  "validation.franchise_owner.error.not_franchise_member":
    "Cet utilisateur ne fait pas partie de la franchise.",
  "validation.restaurant_table.error.code.exists":
    "Une table avec ce numéro existe déjà",
  "validation.restaurant_table.error.code.not_blank":
    "Le code de la table est obligatoire.",
  "validation.restaurant_table.error.restaurant.not_blank":
    "Le restaurant est obligatoire.",
  "validation.qr_code.error.restaurant.not_blank":
    "Le restaurant est obligatoire.",
  "validation.qr_code.error.restaurant.not_found": "Restaurant introuvable.",
  "validation.payment_method.error.name.exists":
    "Un moyen de paiement porte déjà ce nom.",
  "validation.payment_method.error.name.not_blank":
    "Le nom du moyen de paiement est obligatoire.",
  "validation.restaurant_payment_method.error.configuration.invalid":
    "La configuration de ce moyen de paiement est invalide.",
  "validation.restaurant_payment_method.error.payment_method.already_exists":
    "Ce moyen de paiement est déjà activé.",
  "validation.restaurant_payment_method.error.payment_method.not_blank":
    "Choisissez un moyen de paiement.",
  "validation.restaurant_payment_method.error.restaurant.not_blank":
    "Le restaurant est obligatoire.",
  "validation.customer.error.either_order_or_restaurant.both_provided":
    "Données client invalides.",
  "validation.customer.error.either_order_or_restaurant.not_provided":
    "Données client invalides.",
  "validation.customer.error.full_name.too_long": "Le nom est trop long.",
  "validation.customer.error.phone.not_blank":
    "Le numéro de téléphone est obligatoire.",
  "validation.customer_restaurant.error.already_exists":
    "Ce client existe déjà.",
  "validation.dashboard.error.date_format.invalid": "Format de date invalide.",
  "validation.dashboard.error.date_range.invalid":
    "La date de fin doit être après la date de début.",
  "validation.dashboard.error.end_date.empty": "Indiquez la date de fin.",
  "validation.dashboard.error.start_date.empty": "Indiquez la date de début.",
  "validation.dashboard.error.start_date.invalid": "Date de début invalide.",
  "validation.onboarding_request.error.full_name.not_blank":
    "Le nom est obligatoire.",
  "validation.onboarding_request.error.phone.not_blank":
    "Le numéro de téléphone est obligatoire.",
  "validation.onboarding_request.error.restaurant_name.exists":
    "Un restaurant porte déjà ce nom.",
  "validation.onboarding_request.error.restaurant_name.not_blank":
    "Le nom du restaurant est obligatoire.",
  "validation.country.error.iso2.exists": "Ce pays existe déjà.",
  "validation.country.error.iso2.format": "Code pays invalide.",
  "validation.country.error.iso2.length": "Code pays invalide.",
  "validation.country.error.iso2.not_blank": "Le code pays est obligatoire.",
  "validation.country.error.phone_prefix.format": "Indicatif invalide.",
  "validation.country.error.phone_prefix.not_blank":
    "L'indicatif est obligatoire.",
  "validation.currency.error.iso3.exists": "Cette devise existe déjà.",
  "validation.currency.error.iso3.format": "Code devise invalide.",
  "validation.currency.error.iso3.length": "Code devise invalide.",
  "validation.currency.error.iso3.not_blank": "Le code devise est obligatoire.",

  // --- Users, staff, access roles ---------------------------------------------
  "validation.user.error.email.exists":
    "Un utilisateur avec cet email existe déjà !",
  "validation.user.error.email.invalid": "Adresse email invalide.",
  "validation.user.error.phone.exists":
    "Un utilisateur avec ce numéro existe déjà !",
  "validation.user.error.phone.invalid": "Numéro de téléphone invalide.",
  "validation.user.error.phone.not_blank":
    "Le numéro de téléphone est obligatoire.",
  "validation.user.error.phone.international_format_required":
    "Saisissez le numéro au format international, avec l'indicatif du pays (ex. +221 77 123 45 67).",
  "validation.user.error.full_name.not_blank": "Le nom est obligatoire.",
  "validation.user.error.pin.invalid_format":
    "Le code PIN doit contenir uniquement des chiffres.",
  "validation.user.error.pin.not_blank": "Le code PIN est obligatoire.",
  "validation.user.error.franchise.empty": "La franchise est obligatoire.",
  "validation.user.error.restaurant.empty": "Choisissez un restaurant.",
  "validation.user.error.restaurant.not_found": "Restaurant introuvable.",
  "validation.user.error.restaurant.not_in_franchise":
    "Ce restaurant ne fait pas partie de votre franchise.",
  "validation.user.error.roles.empty": "Choisissez au moins un rôle.",
  "validation.user.error.roles.invalid": "Rôle invalide.",
  "validation.user.error.roles.too_many": "Trop de rôles sélectionnés.",
  "validation.user.error.roles.manager_not_assignable":
    "Le rôle manager ne peut pas être attribué ici.",
  "validation.user.error.identifier_change.unchanged":
    "C'est déjà votre identifiant actuel.",
  "validation.user.error.identifier_change.one_required":
    "Modifiez le téléphone ou l'email, un seul à la fois.",
  "validation.user.error.identifier_change.code_required":
    "Saisissez le code reçu.",
  "validation.user.error.identifier_change.code_invalid": "Code incorrect.",
  "validation.user.error.identifier_change.code_expired":
    "Code expiré. Recommencez la modification.",
  "validation.user.error.identifier_change.too_many_attempts":
    "Trop de tentatives. Recommencez la modification.",
  "validation.user.error.identifier_change.none_pending":
    "Aucune modification en attente. Recommencez la modification.",
  "validation.employee.error.user_restaurant.exists":
    "Cet utilisateur fait déjà partie de ce restaurant.",
  "validation.employee_access_role.error.access_role.not_null":
    "Choisissez un rôle d'accès.",
  "validation.employee_access_role.error.already_assigned":
    "Ce rôle d'accès est déjà attribué à cet employé.",
  "validation.employee_access_role.error.employee.not_null":
    "Choisissez un employé.",
  "validation.access_role.error.in_use":
    "Ce rôle est assigné à des employés. Retirez-le de leurs profils avant de le supprimer.",
  "validation.access_role.error.name.not_blank": "Le nom est obligatoire.",
  "validation.access_role.error.permissions.empty":
    "Sélectionnez au moins une permission.",
  "validation.access_role.error.permissions.invalid":
    "Une ou plusieurs permissions sont invalides.",
};
